/* eslint-disable camelcase */
/* eslint-disable consistent-return */
import { postMessageToMobileApp } from 'components/app/helpers';
import { EmailVerificationPayloadObject } from 'components/Auth/EmailVerificationWithLink/types';
import { EditSubUser } from 'components/sub_account/types';
import {
  ALPYNE_API,
  AUTH,
  FRESHDESK_API,
  OAUTH,
  ONMETA_API,
  RECOVER_ACCOUNT,
  SECURITY,
  SUB_ACCOUNTS,
  USER_PROFILE,
  WALLET_API,
} from 'constants/api';
import { ERROR_CODES, isRNWebview, LOGIN_ERROR_CODES } from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS, STORAGE_KEYS } from 'constants/enums';
import { GTM_EVENTS } from 'constants/gtmTracking';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { getDeviceOSTypeForAnalytics } from 'helpers/analytics';
import { formatUrl } from 'helpers/apiClient';
import createAction from 'helpers/createAction';
import { getSHA256HexString } from 'helpers/crypto';
import { getDeviceId } from 'helpers/deviceId';
import { mixpanelStopRecording, trackMixpanelEvent } from 'helpers/mixpanel-init';
import { omit } from 'helpers/ramda';
import { addLogsToSentry } from 'helpers/sentry';
import { isAuthTokenExpired } from 'helpers/user';
import { parseErrorMsg } from 'helpers/utils';
import { isTruthy } from 'ramdax';
import type {
  AccountAnalyticsApiArgs,
  UpdateIncomeSourcePayload,
  DeleteAccountConfirmationPayload,
  DeleteAccountPayload,
  EmailVerificationWithOuathPayloadObject,
  FreshDeskTicketPayload,
  GenerateRecoveryOtpPayload,
  GetMobileOtpArgs,
  GetTradeHistoryArgs,
  KycVendorLinkApiArgs,
  ResendMobileOtpArgs,
  ShareKycDataOnmetaArgs,
  UpdateProfileArgs,
  UpdateProfileAttributePayload,
  VerifyMobileOtpArgs,
  VerifyProfileAttributeOtpPayload,
  VerifyRecoveryOtpPayload,
} from 'types/actions/user';
import IState, { CustomAction, ICustomDispatch, IGetState } from 'types/Istore';
import IUser from 'types/IUser';

import BELL_ALERTS from '../actionTypes/bellAlerts';
import USER, {
  CLOSE_2FA_RESET_POPUP,
  CLOSE_PASSWORD_RESET_POPUP,
  LOGIN_CLICKED,
  NATIVE_USER_DATA,
  NATIVE_VERIFICATION_TOKEN,
  SET_AUTH_SOCKET_CHANNEL_STATUS,
  SET_PHONE_VERIFICATION_TOKEN,
  SET_SHA256_EMAIL_HEX_STRING,
  SET_TRACKIER_ID,
  SHOW_KYC_REFRESH_POPUP,
  SHOW_REDUCE_ONLY_POPUP,
  SIGNUP_CLICKED,
  SWITCH_TO_MAIN_ACCOUNT,
  TRANSFER_CONSTANTS,
  USER_CONNECTED,
  USER_DISCONNECTED,
  USER_LOGIN_REDIRECTION,
  USER_SIGNUP_FAIL,
  USER_SIGNUP_SUCCESS,
  ACCOUNT_REFRESH_TRIGGER_LAST_CLICKED,
  UNAUTHORIZED,
  CHECK_IF_AUTH_TOKEN_EXPIRED,
  SET_TPSL_CHART_NOTIFICATION_SHOWN,
  SET_PLUS_BUTTON_NOTIFICATION_SHOWN,
  SET_MINI_CHART_TOGGLE_SHOWN,
  SET_SPOT_IMPORTANT_INFO_SHOWN,
} from '../actionTypes/user';
import VOLUME_HISTORY from '../actionTypes/volumeHistory';

// export const updateHeartbeatTs = createAction(UPDATE_HEARTBEAT_TS);
export const userConnected = createAction(USER_CONNECTED);
// export const socketConnected = createAction(SOCKET_CONNECTED);
export const userDisconnected = createAction(USER_DISCONNECTED);
export const userSignupSuccess = createAction(USER_SIGNUP_SUCCESS);
export const userLoginRedirection = createAction(USER_LOGIN_REDIRECTION);
export const userSignupFail = createAction(USER_SIGNUP_FAIL);
export const setUnauthorized = createAction(UNAUTHORIZED);
export const checkIfAuthTokenExpired = createAction(CHECK_IF_AUTH_TOKEN_EXPIRED);

export const setUserTrackierId = createAction(SET_TRACKIER_ID);
export const setAuthSocketChannelStatus = createAction(SET_AUTH_SOCKET_CHANNEL_STATUS);

export const isAuthenticated = (user: { token?: string }) => isTruthy(user?.token);

export function authAction(action: CustomAction) {
  return (dispatch: ICustomDispatch<IState>, getState: IGetState) => {
    const { user } = getState();

    if (user.token && isAuthenticated(user)) {
      // Check if token is expired before dispatching
      if (isAuthTokenExpired(user)) {
        console.warn('Token expired, logging out user');
        return dispatch(setUnauthorized({}));
      }
      return dispatch(action);
    }
    console.warn(
      'user is not authenticated and requesting a private resource',
      action?.types?.LOAD
    );
  };
}

export function loginClicked(payload: any) {
  return {
    type: LOGIN_CLICKED,
    payload,
  };
}

export function signupClicked(payload: any) {
  return {
    type: SIGNUP_CLICKED,
    payload,
  };
}

export function switchToMainAccount() {
  return {
    type: SWITCH_TO_MAIN_ACCOUNT,
  };
}

export function showKycRefreshPopup(payload: any) {
  return {
    type: SHOW_KYC_REFRESH_POPUP,
    payload,
  };
}

export function showReduceOnlyPopup(payload: any) {
  return {
    type: SHOW_REDUCE_ONLY_POPUP,
    payload,
  };
}

export function closePasswordResetModal() {
  return {
    type: CLOSE_PASSWORD_RESET_POPUP,
  };
}

export function close2faResetModal() {
  return {
    type: CLOSE_2FA_RESET_POPUP,
  };
}

export function login(
  email: string,
  password: string,
  device_id: string,
  recaptcha_response: string,
  extraData: {
    page_name: string;
    type: string;
  } = {
    page_name: '',
    type: '',
  }
): CustomAction {
  const payload = { email, password, device_id, recaptcha_response } as Record<
    string,
    any
  >;
  if (isRNWebview()) {
    const key = 'source';
    payload[key] = 'mobile_app';
  }

  payload.device_os_type = getDeviceOSTypeForAnalytics();
  return {
    types: USER.LOGIN,
    payload,
    promise: ({ request }) =>
      request
        .post(USER_PROFILE.LOGIN, {
          data: payload,
        })
        .catch(error => {
          const result = parseErrorMsg(error);
          const commonTrackingData = {
            email,
            error_code: (result as any).error.code,
            ...extraData,
          };

          if ((result as any)?.error?.code !== ERROR_CODES.PENDING_USER_CONSENT) {
            trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_IN.FAIL, commonTrackingData);
          }

          if (!LOGIN_ERROR_CODES.includes((result as any).error.code)) {
            addLogsToSentry(
              {
                requestPayload: omit(['password'], payload),
                error: JSON.stringify(error),
                ...commonTrackingData,
              },
              'Login Unhandled Error'
            );
          }

          throw error;
        }),
  };
}

export function loginmfa(
  pre_2fa_token: string,
  mfa_code: string,
  email_otp: string
): CustomAction {
  const payload = { pre_2fa_token, mfa_code, email_otp } as Record<string, any>;

  if (isRNWebview()) {
    const key = 'source';
    payload[key] = 'mobile_app';
  }

  payload.device_os_type = getDeviceOSTypeForAnalytics();

  return {
    types: USER.LOGINMFA,
    promise: ({ request }) => request.post(USER_PROFILE.LOGIN_MFA, { data: payload }),
  };
}

type LoginWithConsentPayload = {
  jwt_token: string;
  trackier_click_id: string;
  device_os_type: string;
};
export function loginWithConsent(body: LoginWithConsentPayload): CustomAction {
  return {
    types: USER.LOGIN,
    promise: ({ request }) => request.post(AUTH.LOGIN.LOGIN_WITH_CONSENT, { data: body }),
  };
}

export function resendLoginEmailCode(
  pre_2fa_token: string,
  recaptcha_response: any
): CustomAction {
  const payload = {
    pre_2fa_token,
    recaptcha_response,
  };
  return {
    types: USER.RESEND_LOGIN_EMAIL_CODE,
    promise: ({ request }) =>
      request.post(USER_PROFILE.RESEND_LOGIN_EMAIL_CODE, { data: payload }),
  };
}

export function sendLoginMobileOTP(pre_2fa_token: string): CustomAction {
  const payload = {
    pre_2fa_token,
  };

  return {
    types: USER.SEND_OTP_ON_PHONE_NUMBER,
    promise: ({ request }) =>
      request.post(USER_PROFILE.SEND_OTP_ON_PHONE_NUMBER, { data: payload }),
  };
}

export function loginEmailVerification(
  pre_2fa_token: string,
  email_otp: string,
  recaptcha_response: any,
  phoneOTPVerification: boolean = false
): CustomAction {
  const payload = {
    pre_2fa_token,
    recaptcha_response,
  } as Record<string, any>;

  payload.device_os_type = getDeviceOSTypeForAnalytics();

  let uri = USER_PROFILE.LOGIN_EMAIL_VERIFICATION;

  if (phoneOTPVerification) {
    uri = USER_PROFILE.PHONE_OTP_VERIFICATION;
    payload.phone_otp = email_otp;
  } else {
    payload.email_otp = email_otp;
  }

  const types = phoneOTPVerification
    ? USER.PHONE_OTP_VERIFICATION
    : USER.LOGIN_EMAIL_VERIFICATION;

  return {
    types,
    promise: ({ request }) => request.post(uri, { data: payload }),
  };
}

export function oAuthLoginSuccess(response) {
  return {
    type: USER.LOGIN_EMAIL_VERIFICATION.SUCCESS,
    result: response,
  };
}

export function signup(data): CustomAction {
  return {
    types: USER.SIGNUP,
    email: data.email,
    promise: ({ request }) => request.post(USER_PROFILE.REGISTER_SIGNUP, { data }),
  };
}

export function checkReferralCode(referralCode: string): CustomAction {
  return {
    types: USER.CHECK_REFERRAL_CODE,
    promise: ({ request }) =>
      request.get(USER_PROFILE.CHECK_REFERRAL_CODE, {
        params: {
          referral_code: referralCode,
        },
      }),
  };
}

export function logout() {
  const data: Record<string, any> = {
    device_id: getDeviceId(),
    device_os_type: getDeviceOSTypeForAnalytics(),
  };
  if (isRNWebview()) {
    localStorage.removeItem('mobileDeviceId');
  }

  if (isRNWebview()) {
    postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT);
  }

  localStorage.removeItem(STORAGE_KEYS.TRADE_SUCCESS_SHARE_CARD_DATA_LOADED);

  mixpanelStopRecording();

  // Reset Chatwoot widget session on logout
  window.$chatwoot?.reset?.();

  return authAction({
    types: USER.LOGOUT,
    promise: ({ request }) =>
      request.post(USER_PROFILE.LOGOUT, {
        data,
      }),
  });
}

export const logoutFromSubAccount = token => {
  const deviceId = localStorage.getItem('mobileDeviceId');
  if (isRNWebview() && deviceId) {
    localStorage.removeItem('mobileDeviceId');
  }

  mixpanelStopRecording();

  // Reset Chatwoot widget session on logout
  window.$chatwoot?.reset?.();

  return fetch(formatUrl(USER_PROFILE.LOGOUT), {
    headers: { Authorization: `${token}` },
    method: 'POST',
    ...(isRNWebview() && deviceId
      ? { body: JSON.stringify({ device_id: deviceId }) }
      : {}),
  });
};

export function updateProfile(apiData: UpdateProfileArgs, showAlert = true) {
  return authAction({
    types: USER.UPDATE_PROFILE,
    promise: ({ request }) => request.post(USER_PROFILE.UPDATE, { data: apiData }),
    showAlert,
  });
}

export function getTradeSuccessCardsDetails(userId: number) {
  return authAction({
    types: USER.GET_TRADE_SUCCESS_CARD_DETAILS,
    promise: ({ request }) =>
      request.get(USER_PROFILE.TRADE_SUCCESS_CARD, {
        params: {
          user_id: userId,
        },
      }),
  });
}

export function addPhoneNumber(data: GetMobileOtpArgs) {
  return authAction({
    types: USER.ADD_PHONE_NUMBER,
    promise: ({ request }) => request.post(USER_PROFILE.ADD_PHONE_NUMBER, { data }),
  });
}

export function addPhoneVerificationToken(data: string) {
  return {
    type: SET_PHONE_VERIFICATION_TOKEN,
    payload: data,
  };
}

export function addNativeAppVerificationToken(data: string) {
  return {
    type: NATIVE_VERIFICATION_TOKEN,
    payload: data,
  };
}

export function userNativeLoginSuccess(data: IUser) {
  return {
    type: NATIVE_USER_DATA,
    payload: data,
  };
}

export function verifyMobileNumber(data: VerifyMobileOtpArgs) {
  return authAction({
    types: USER.VERIFY_PHONE_NUMBER_OTP,
    promise: ({ request }) =>
      request.post(USER_PROFILE.VERIFY_PHONE_NUMBER_OTP, { data }),
  });
}

export function resendPhoneNumberOTP(data: ResendMobileOtpArgs) {
  return authAction({
    types: USER.RESEND_PHONE_NUMBER_OTP,
    promise: ({ request }) =>
      request.post(USER_PROFILE.RESEND_PHONE_NUMBER_OTP, { data }),
  });
}

export function getBellAlerts() {
  return authAction({
    types: BELL_ALERTS.GET_BELL_ALERTS,
    promise: ({ request }) => request.get(USER_PROFILE.BELL_ALERTS),
  });
}

export function getVolumeHistory(params) {
  return {
    types: VOLUME_HISTORY.GET_VOLUME_HISTORY,
    promise: ({ request }) =>
      request.get(`${USER_PROFILE.VOLUME_HISTORY}?timeperiod=${params}`),
  };
}

export function markAsRead(commonBellAlertIds: number[], userBellAlertIds: number[]) {
  return authAction({
    types: BELL_ALERTS.MARK_AS_READ,
    promise: ({ request }) =>
      request.post(USER_PROFILE.BELL_ALERTS_READ, {
        data: {
          bell_alert_ids: commonBellAlertIds,
          user_bell_alert_ids: userBellAlertIds,
        },
      }),
  });
}

export function getUserKycDocuments() {
  return authAction({
    types: USER.GET_KYC_DOCUMENTS,
    promise: ({ request }) => request.get(USER_PROFILE.GET_KYC_DOCUMENTS),
  });
}

export function getConsentPopupDetails(vendor: string) {
  return authAction({
    types: USER.GET_CONSENT_POPUP_DETAILS,
    promise: ({ request }) =>
      request.post(USER_PROFILE.GET_CONSENT_POPUP_DETAILS, {
        data: {
          vendor,
        },
      }),
  });
}

export function refreshKyc() {
  return authAction({
    types: USER.REFRESH_KYC,
    promise: ({ request }) => request.post(USER_PROFILE.REFRESH_KYC),
  });
}

export function getMobileOtp(data: GetMobileOtpArgs) {
  return {
    types: USER.GET_MOBILE_VERIFICATION_OTP,
    promise: ({ request }) => request.post(USER_PROFILE.PHONE_NUMBER, { data }),
  };
}

export function verifyMobileOtp(data: VerifyMobileOtpArgs) {
  return {
    types: USER.VERIFY_MOBILE_OTP,
    promise: ({ request }) => request.post(USER_PROFILE.VERIFY_PHONE_NUMBER, { data }),
  };
}

export function resendMobileOtp(data: ResendMobileOtpArgs) {
  return {
    types: USER.RESEND_MOBILE_OTP,
    promise: ({ request }) => request.post(USER_PROFILE.RESEND_PHONE_NUMBER, { data }),
  };
}

export function getKycStatus() {
  return authAction({
    types: USER.GET_KYC_STATUS,
    promise: ({ request }) => request.get(USER_PROFILE.KYC_STATUS),
  });
}

export function getWithdrawalsPendingCount() {
  return {
    types: USER.WITHDRAWAL_PENDING_COUNT,
    promise: ({ request }) => request.get(USER_PROFILE.WITHDRAWAL_PENDING),
  };
}

export function getKycCertificates() {
  return authAction({
    types: USER.GET_KYC_CERTIFICATES,
    promise: ({ request }) => request.get(USER_PROFILE.KYC_CERTIFICATES),
  });
}

export function changePassword(data) {
  return authAction({
    types: USER.CHANGE_PASSWORD,
    promise: ({ request }) => request.post(USER_PROFILE.CHANGE_PASSWORD, { data }),
  });
}

export function changePasswordNew(data) {
  return authAction({
    types: USER.CHANGE_PASSWORD_NEW,
    promise: ({ request }) => request.post(USER_PROFILE.CHANGE_PASSWORD_NEW, { data }),
  });
}

export function changePasswordOtpValidation(data) {
  return authAction({
    types: USER.CHANGE_PASSWORD_VALIDATION,
    promise: ({ request }) =>
      request.post(USER_PROFILE.CHANGE_PASSWORD_VALIDATION, { data }),
  });
}

export function changePasswordVerificationCode(data) {
  return authAction({
    types: USER.CHANGE_PASSWORD_VERIFICATION_CODE,
    promise: ({ request }) =>
      request.post(USER_PROFILE.CHANGE_PASSWORD_VERIFICATION_CODE, { data }),
  });
}

export function enableMFA({ otp, googleSecret, email_code }) {
  return authAction({
    types: USER.ENABLE_MFA,
    promise: ({ request }) =>
      request.post(USER_PROFILE.ENABLE_MFA, {
        data: {
          mfa_code: otp,
          email_code,
          google_secret: googleSecret,
          device_id: getDeviceId(),
          device_os_type: getDeviceOSTypeForAnalytics(),
        },
      }),
  });
}

export function enableMFASendEmailCode() {
  return authAction({
    types: USER.ENABLE_MFA_SEND_EMAIL_OTP,
    promise: ({ request }) => request.post(USER_PROFILE.ENABLE_MFA_SEND_EMAIL_CODE),
  });
}

export function disableMFA(mfa_code: string, recaptcha_response: string) {
  return authAction({
    types: USER.DISABLE_MFA,
    promise: ({ request }) =>
      request.post(USER_PROFILE.DISABLE_MFA, {
        data: {
          mfa_code,
          device_id: getDeviceId(),
          device_os_type: getDeviceOSTypeForAnalytics(),
          recaptcha_response,
        },
      }),
  });
}

interface ResetMfaPayload {
  mfa_code: string;
  google_secret: string;
  new_mfa_code: string;
  email_code: string;
}

export function resetMFA(payload: ResetMfaPayload) {
  return authAction({
    types: USER.RESET_MFA,
    promise: ({ request }) =>
      request.post(USER_PROFILE.RESET_MFA, {
        data: {
          ...payload,
          device_id: getDeviceId(),
          device_os_type: getDeviceOSTypeForAnalytics(),
        },
      }),
  });
}

export function getUnverifiedUsersWithdrawalLimit() {
  return authAction({
    types: USER.UNVERIFIED_USERS_WITHDRAWAL_LIMIT,
    promise: ({ request }) => request.get(USER_PROFILE.UNVERIFIED_USERS_WITHDRAWAL_LIMIT),
  });
}

// Deprecated
export function markIntroAsSeen(): CustomAction {
  return {
    types: USER.MARK_INTRO_SEEN,
    promise: ({ request }) => request.post('/profile/seenIntro'),
  };
}

export function forgotPassword(email: string, recaptcha_response: string): CustomAction {
  return {
    types: USER.FORGOT_PASSWORD,
    promise: ({ request }) =>
      request.post(USER_PROFILE.FORGOT_PASSWORD, {
        data: {
          email,
          recaptcha_response,
        },
      }),
  };
}

export function resetPassword(
  email: string,
  code: string | number,
  new_password: string
): CustomAction {
  return {
    types: USER.RESET_PASSWORD,
    promise: ({ request }) =>
      request.post(USER_PROFILE.RESET_PASSWORD, {
        data: {
          email,
          code,
          new_password,
          device_id: getDeviceId(),
          device_os_type: getDeviceOSTypeForAnalytics(),
        },
      }),
  };
}

export function sendCodeApiKey() {
  return authAction({
    fetch_main_acc_token: true,
    promise: ({ request }) => request.post(USER_PROFILE.SEND_CODE_API_KEY, {}),
  });
}

export function createApiKey(data) {
  return authAction({
    types: USER.CREATE_API_KEY,
    fetch_main_acc_token: true,
    promise: ({ request }) => request.post(USER_PROFILE.CREATE_API_KEY, { data }),
  });
}

export function updateAPIKey(data) {
  return authAction({
    types: USER.UPDATE_API_KEY,
    fetch_main_acc_token: true,
    promise: ({ request }) => request.put(USER_PROFILE.CREATE_API_KEY, { data }),
  });
}

export function getApiKeys() {
  return authAction({
    types: USER.GET_API_KEYS,
    promise: ({ request }) => request.get(USER_PROFILE.API_KEYS),
  });
}

export function deleteApiKey(data) {
  return authAction({
    types: USER.DELETE_API_KEY,
    fetch_main_acc_token: true,
    promise: ({ request }) => request.delete(USER_PROFILE.DELETE_API_KEY, { data }),
  });
}

export function getReferralData() {
  return authAction({
    types: USER.MY_REFERRALS,
    promise: ({ request }) => request.get(USER_PROFILE.MY_REFERRALS),
  });
}

export function getOffers(): CustomAction {
  return {
    types: USER.GET_OFFERS,
    promise: ({ request }) => request.get(USER_PROFILE.OFFERS),
  };
}

export function applyPromo(data): CustomAction {
  return {
    types: USER.APPLY_PROMO,
    promise: ({ request }) => request.post(USER_PROFILE.OFFERS_APPLY_PROMO, { data }),
  };
}

export function removePromo(data): CustomAction {
  return {
    types: USER.REMOVE_PROMO,
    promise: ({ request }) => request.post(USER_PROFILE.OFFERS_REMOVE_PROMO, { data }),
  };
}

export function getAppliedPromos(): CustomAction {
  return {
    types: USER.GET_APPLIED_PROMOS,
    promise: ({ request }) => request.get(USER_PROFILE.OFFERS_APPLIED),
  };
}

export function getActivityList() {
  return authAction({
    types: USER.GET_ACCOUNT_ACTIVITY,
    promise: ({ request }) => request.get(USER_PROFILE.HISTORY),
  });
}

export function getActivieLogins() {
  return authAction({
    types: USER.GET_ACTIVE_SESSIONS,
    promise: ({ request }) => request.get(SECURITY.ACTIVE_LOGINS),
  });
}

export function getAddresses(user_id) {
  return authAction({
    types: USER.GET_WITHDRAWAL_ADDRESSES,
    promise: ({ request }) => request.get(SECURITY.ADDRESS(user_id)),
  });
}

export function createWithdrawalAddress(data) {
  return authAction({
    types: USER.CREATE_WITHDRAWAL_ADDRESS,
    promise: ({ request }) =>
      request.post(SECURITY.WITHDRAWAL_ADDRESS, {
        data,
      }),
  });
}

export function updateWithdrawalAddress(data: unknown) {
  return authAction({
    types: USER.UPDATE_WITHDRAWAL_ADDRESS,
    promise: ({ request }) =>
      request.put(SECURITY.WITHDRAWAL_ADDRESS, {
        data,
      }),
  });
}

export function deleteWithdrawalAddress(data) {
  return authAction({
    types: USER.DELETE_WITHDRAWAL_ADDRESS,
    params: data,
    promise: ({ request }) =>
      request.delete(SECURITY.WITHDRAWAL_ADDRESS, {
        data,
      }),
  });
}

export function logoutFromAllDevices({
  showErrorAlert = true,
}: { showErrorAlert?: boolean } = {}) {
  const data: Record<string, any> = {
    device_id: getDeviceId(),
    device_os_type: getDeviceOSTypeForAnalytics(),
  };
  if (isRNWebview()) {
    postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT, {
      action: 'logout_all_devices',
    } as never);
  }

  // Reset Chatwoot widget session on logout
  window.$chatwoot?.reset?.();

  return authAction({
    types: USER.LOGOUT_FROM_ALL_DEVICES,
    promise: ({ request }) =>
      request.post(SECURITY.LOGOUT_ALL, {
        data,
      }),
    showErrorAlert,
  });
}

export function resendLink(email): CustomAction {
  return {
    types: USER.RESEND_LINK,
    promise: ({ request }) =>
      request.post(USER_PROFILE.REGISTER_RESEND_VERIFICATION_CODE, {
        data: { email },
      }),
  };
}

/* eslint-disable no-param-reassign */
export function registerVerification(payload: {
  otp: string;
  email: string;
  device_id: string;
  trackier_click_id?: string;
  recaptcha_response: string;
  source?: string;
  device_os_type?: string;
}): CustomAction {
  const payloadWithTrackingKeys = { ...payload };
  if (isRNWebview()) {
    payloadWithTrackingKeys.source = 'mobile_app';
  }

  payloadWithTrackingKeys.device_os_type = getDeviceOSTypeForAnalytics();

  return {
    types: USER.LOGIN,
    promise: ({ request }) =>
      request.post(USER_PROFILE.REGISTER_VERIFY, {
        data: payloadWithTrackingKeys,
      }),
  };
}
/* eslint-enable */

export function updateUserPreference(preferences, payload?: any) {
  // const payload = preferences.preferences.favorites ? 'favorites' : '';
  return authAction({
    types: USER.UPDATE_PREFERENCES,
    payload: payload ? { ...payload } : { disable_notification: false },
    promise: ({ request }) =>
      request.post(USER_PROFILE.UPDATE_USER_PREFERENCES, {
        data: preferences,
      }),
  });
}

// eslint-disable-next-line camelcase
export function refreshToken(use_main_token = false) {
  return authAction({
    types: USER.REFRESH_TOKEN,
    // eslint-disable-next-line camelcase
    fetch_main_acc_token: use_main_token,

    promise: ({ request }) => request.get(USER_PROFILE.REFRESH_TOKEN),
  });
}

export function getUserProfile() {
  return authAction({
    types: USER.GET_USER_PROFILE,
    promise: ({ request }) => request.get(USER_PROFILE.GET_USER_PROFILE),
  });
}

export function getAccountAnalyticsPNLSummary() {
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_PNL_SUMMARY,
    promise: ({ request }) => request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_PNL_SUMMARY),
  });
}

export function getAccountAnalyticsPNLChartData(payload: AccountAnalyticsApiArgs) {
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_PNL_CHART_DATA,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_PNL_CHART_DATA, payload),
  });
}

export function getAccountAnalyticsVolume(payload: AccountAnalyticsApiArgs) {
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_VOLUME,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_VOLUME, payload),
  });
}
export function getAccountAnalyticsFeesPaid(payload: AccountAnalyticsApiArgs) {
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_FEES_PAID,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_FEES_PAID, payload),
  });
}

export function getAccountAnalyticsKeyMetrics() {
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_DAILY_EQUITY_CHANGE,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_DAILY_EQUITY_CHANGE),
  });
}

export function getAccountAnalyticsStatistics(payload: AccountAnalyticsApiArgs) {
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_STATISTICS,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_STATISTICS, payload),
  });
}

export function emailVerificationUsingLink(payload: EmailVerificationPayloadObject) {
  return {
    types: USER.EMAIL_VERIFICATION_WITH_LINK,
    promise: ({ request }) =>
      request.post(USER_PROFILE.VERIFY_EMAIL_WITH_LINK, { data: payload }),
  };
}

export function emailVerificationWithOuath(
  payload: EmailVerificationWithOuathPayloadObject
) {
  return {
    types: USER.EMAIL_VERIFICATION_WITH_OAUTH,
    promise: ({ request }) => request.post(OAUTH.VERIFY_WITH_OAUTH, payload),
  };
}

export function getAccountAnalyticsDetoEarnChart(payload) {
  const { params } = payload;
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_DETO_CHART,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_DETO_CHART, {
        params,
      }),
  });
}

export function getAccountAnalyticsPNLChart(payload) {
  const { params } = payload;
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_PNL_CHART,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GET_ACCOUNT_ANALYTICS_PNL_CHART, {
        params,
      }),
  });
}

export function getAccountAnalyticsActivity(payload) {
  return authAction({
    types: USER.GET_ACCOUNT_ANALYTICS_ACTIVITY,
    promise: ({ request }) =>
      request.get(`${USER_PROFILE.GET_ACCOUNT_ANALYTICS_ACTIVITY}`, payload),
  });
}

export function getSubAccounts() {
  return authAction({
    types: USER.GET_SUB_ACCOUNTS,
    fetch_main_acc_token: true,
    promise: ({ request }) => request.get(SUB_ACCOUNTS.GET_SUB_ACCOUNTS),
  });
}

export function getSubAccountsWallets() {
  return authAction({
    types: USER.GET_SUB_ACCOUNTS_WALLETS,
    fetch_main_acc_token: true,
    promise: ({ request }) => request.get(SUB_ACCOUNTS.GET_SUB_ACCOUNTS_WALLETS),
  });
}

export function createSubAccount(data: any) {
  return authAction({
    types: USER.CREATE_SUB_ACCOUNT,
    fetch_main_acc_token: true,
    promise: ({ request }) =>
      request.post(SUB_ACCOUNTS.SUB_ACCOUNT, {
        data,
      }),
  });
}

export function transferFundsSubAccount(data) {
  return authAction({
    types: USER.TRANSFER_FUNDS_SUB_ACCOUNTS,
    fetch_main_acc_token: true,
    payload: data,
    promise: ({ request }) =>
      request.post(SUB_ACCOUNTS.TRNASFER_FUNDS_SUB_ACCOUNT, {
        data,
      }),
  });
}

export function getTransferFundsHistorySubAccount(payload) {
  return authAction({
    types: USER.TRANSFER_FUNDS_HISTORY_SUB_ACCOUNTS,
    params: {
      backgroundFetch: payload.backgroundFetch,
    },
    promise: ({ request }) =>
      request.get(SUB_ACCOUNTS.TRNASFER_FUNDS_HISTORY_SUB_ACCOUNT, {
        params: payload,
        keepHeaders: true,
      }),
  });
}

export function getSubAccountWalletBalance(data) {
  return authAction({
    types: USER.GET_BALANCE_SUB_ACCOUNT,
    fetch_main_acc_token: true,
    promise: ({ request }) =>
      request.get(WALLET_API.WITHDRAWAL_LIMITS, {
        params: {
          asset_symbol: data.asset_symbol,
          sub_account_user_id: data.sub_account_user_id,
        },
      }),
  });
}

export function getAccountToken(data) {
  return authAction({
    types: USER.GET_ACCOUNT_TOKEN,
    fetch_main_acc_token: true,
    promise: ({ request }) =>
      request.post(SUB_ACCOUNTS.GET_ACCOUNT_TOKEN, {
        data,
      }),
  });
}

export function updateSubAccount(data: EditSubUser) {
  return authAction({
    types: USER.UPDATE_SUB_ACCOUNT,
    fetch_main_acc_token: true,
    promise: ({ request }) =>
      request.put(SUB_ACCOUNTS.SUB_ACCOUNT, {
        data,
      }),
  });
}
export function resetTransferFundHistory() {
  return {
    type: TRANSFER_CONSTANTS.RESET_TRANSFER_FUND_HISTORY,
    payload: [],
  };
}

export function getKYCVendorLink(args?: KycVendorLinkApiArgs) {
  return authAction({
    types: USER.GET_KYC_VENDOR_LINK,
    promise: ({ request }) => request.get(USER_PROFILE.GET_KYC_VENDOR_LINK, args),
  });
}

const createAlpyneUserKYC = () => {
  return authAction({
    types: USER.CREATE_ALPYNE_USER_KYC,
    promise: ({ request }) => request.post(ALPYNE_API.CREATE_USER),
  });
};

const shareKycDataOnmeta = (payload: ShareKycDataOnmetaArgs) => {
  return authAction({
    types: USER.SHARE_KYC_DATA_ONMETA,
    promise: ({ request }) => request.post(ONMETA_API.SHARE_KYC_DATA_ONRAMP, payload),
  });
};

export const getTradeHistory = (params: GetTradeHistoryArgs) => {
  return authAction({
    types: USER.GET_TRADE_HISTORY,
    promise: ({ request }) => request.get(USER_PROFILE.GET_TRADE_HISTORY, params),
  });
};

function raiseFreshdeskTicket() {
  return authAction({
    types: USER.FRESH_DESK_TICKET_REQUEST_OTP,
    promise: ({ request }) => request.post(FRESHDESK_API.SEND_OTP),
  });
}

function resendFreshDeskTicketOtp() {
  return authAction({
    types: USER.FRESH_DESK_TICKET_REQUEST_OTP,
    promise: ({ request }) => request.post(FRESHDESK_API.RESEND_OTP),
  });
}

function validateFreshDeskOtp(payload: FreshDeskTicketPayload) {
  return authAction({
    types: USER.FRESH_DESK_TICKET_VALIDATION,
    promise: ({ request }) => request.post(FRESHDESK_API.VALIDATE_OTP, payload),
  });
}

function getAccountEquityChartData(payload: AccountAnalyticsApiArgs) {
  return authAction({
    types: USER.ACCOUNT_EQUITY_CHART,
    promise: ({ request }) =>
      request.get(USER_PROFILE.ACCOUNT_ANALYTICS_EQUITY_CHART, payload),
  });
}

function getDepositAndWithdrawalsChartData(payload: AccountAnalyticsApiArgs) {
  return authAction({
    types: USER.DEPOSIT_WITHDRAWALS_CHART,
    promise: ({ request }) => request.get(USER_PROFILE.DEPOSIT_AND_WITHDRAWALS, payload),
  });
}

function fetchUserIpAddress() {
  return authAction({
    types: USER.IP_ADDRESS,
    promise: ({ request }) => request.get(USER_PROFILE.IP_ADDRESS),
  });
}

const bankAccountAdded = (payload: { isBankAccountAdded: boolean }) => {
  return {
    type: USER.BANK_ACCOUNT_ADDED,
    payload,
  };
};

function deleteAccount(payload: DeleteAccountPayload) {
  return authAction({
    types: USER.DELETE_ACCOUNT,
    promise: ({ request }) =>
      request.post(USER_PROFILE.DELETE_ACCOUNT, {
        ...payload,
        data: {
          ...payload.data,
          device_id: getDeviceId(),
          device_os_type: getDeviceOSTypeForAnalytics(),
        },
      }),
  });
}

function deleteAccountConfirmation(payload: DeleteAccountConfirmationPayload) {
  return authAction({
    types: USER.DELETE_ACCOUNT_CONFIRMATION,
    promise: ({ request }) =>
      request.get(USER_PROFILE.DELETE_ACCOUNT_CONFIRMATION, payload),
  });
}

function generateRecoverOtp(payload: GenerateRecoveryOtpPayload) {
  return {
    types: USER.GENERATE_RECOVERY_OTP,
    promise: ({ request }) =>
      request.get(RECOVER_ACCOUNT.GENERATE_OTP, {
        params: payload.data,
      }),
  };
}

function verifyRecoveryOtp(payload: VerifyRecoveryOtpPayload) {
  return {
    types: USER.VERIFY_RECOVERY_OTP_AND_GET_VENDOR,
    promise: ({ request }) => {
      return request.get(RECOVER_ACCOUNT.VERIFY_AND_GET_VENDOR, {
        params: {
          ...payload.data,
          device_id: getDeviceId(),
          device_os_type: getDeviceOSTypeForAnalytics(),
        },
      });
    },
  };
}

function request2faLostAuthenticatedUser() {
  return authAction({
    types: USER.AUTHENTICATED_USER_LOST_2FA,
    promise: ({ request }) =>
      request.post(USER_PROFILE.AUTHENTICATED_USER_LOST_2FA, {
        data: {
          device_id: getDeviceId(),
          device_os_type: getDeviceOSTypeForAnalytics(),
        },
      }),
  });
}

function setSHA256EmailHex() {
  return async (dispatch: ICustomDispatch<IState>, getState: IGetState) => {
    const { user } = getState();

    try {
      const sha256EmailHexString = await getSHA256HexString(user.email!);

      dispatch({
        type: SET_SHA256_EMAIL_HEX_STRING,
        payload: {
          sha256EmailHexString,
        },
      });
    } catch (err) {
      addLogsToSentry(user, err);
    }
  };
}

function getGtmEventData(params: { event_name: GTM_EVENTS }) {
  return authAction({
    types: USER.GTM_EVENTS,
    promise: ({ request }) =>
      request.get(USER_PROFILE.GTM_EVENTS, {
        params,
      }),
  });
}

const updateProfileAttribute = (payload: UpdateProfileAttributePayload) => {
  return authAction({
    types: USER.UPDATE_PROFILE_ATTRIBUTE,
    promise: ({ request }) =>
      request.post(USER_PROFILE.UPDATE_PROFILE_ATTRIBUTE, payload),
  });
};

const verifyProfileUpdateOtp = (payload: VerifyProfileAttributeOtpPayload) => {
  return authAction({
    types: USER.VERIFY_PROFILE_UPDATES_OTP,
    promise: ({ request }) =>
      request.post(USER_PROFILE.VERIFY_PROFILE_UPDATES_OTP, payload),
  });
};

const setAccountRefreshTriggerLastClicked = createAction(
  ACCOUNT_REFRESH_TRIGGER_LAST_CLICKED
);

const updateIncomeSource = (payload: UpdateIncomeSourcePayload) => {
  return authAction({
    types: USER.UPDATE_INCOME_SOURCE,
    promise: ({ request }) => request.post(USER_PROFILE.UPDATE_INCOME_SOURCE, payload),
  });
};

export const setTPSLChartNotificationShown = createAction(
  SET_TPSL_CHART_NOTIFICATION_SHOWN
);

export const setPlusButtonNotificationShown = createAction(
  SET_PLUS_BUTTON_NOTIFICATION_SHOWN
);

export const setMiniChartToggleShown = createAction(SET_MINI_CHART_TOGGLE_SHOWN);
export const setSpotImportantInfoShown = createAction(SET_SPOT_IMPORTANT_INFO_SHOWN);

export {
  bankAccountAdded,
  createAlpyneUserKYC,
  deleteAccount,
  deleteAccountConfirmation,
  fetchUserIpAddress,
  generateRecoverOtp,
  getAccountEquityChartData,
  getDepositAndWithdrawalsChartData,
  getGtmEventData,
  raiseFreshdeskTicket,
  request2faLostAuthenticatedUser,
  resendFreshDeskTicketOtp,
  setSHA256EmailHex,
  shareKycDataOnmeta,
  updateProfileAttribute,
  validateFreshDeskOtp,
  verifyProfileUpdateOtp,
  verifyRecoveryOtp,
  setAccountRefreshTriggerLastClicked,
  updateIncomeSource,
};
